import { XmarkIcon } from '@shared/assets/icons';
import type { MessagesStats } from '@pages/event/api/useFetchMessagesStats';
import { SendContentType } from '@pages/event/api/useSendMessages';
import { IconButton } from '@shared/ui/icon-button';
import { Button } from '@shared/ui/kit';
import { ModalWrapper } from '@shared/ui/modal-wrapper';
import { useCustomScrollbar } from '@shared/hooks/useCustomScrollbar';
import clsx from 'clsx';
import { FC, useCallback, useEffect, useState } from 'react';
import type { StatisticsModalView, UnsentErrorChannel } from './lib/types';
import styles from './send-statistics-modal.module.scss';
import { ModalBodyContent } from './ui/modal-body-content';

const MODAL_SETTINGS = {
  settingsContainer: { height: 'auto', width: '100%', padding: '0px', overflow: 'hidden' as const },
  settingsHeader: { padding: '0px' },
  settingsFooter: { padding: '0px' },
  settings: {
    overflow: 'hidden' as const,
    width: '490px',
    maxWidth: 'calc(100vw - 48px)',
    boxSizing: 'border-box' as const,
  },
};

interface SendStatisticsModalProps {
  active: boolean;
  messagesStats: MessagesStats | null;
  contentType?: SendContentType;
  initialView?: StatisticsModalView;
  initialErrorChannel?: UnsentErrorChannel;
  onClose: () => void;
}

export const SendStatisticsModal: FC<SendStatisticsModalProps> = ({
  active,
  messagesStats,
  contentType = SendContentType.Invite,
  initialView = 'statistics',
  initialErrorChannel,
  onClose,
}) => {
  const [view, setView] = useState<StatisticsModalView>(initialView);
  const [errorChannel, setErrorChannel] = useState<UnsentErrorChannel | null>(
    initialView === 'unsent' ? initialErrorChannel ?? null : null
  );

  useEffect(() => {
    setView(initialView);
    setErrorChannel(initialView === 'unsent' ? initialErrorChannel ?? null : null);
  }, [active, initialView, initialErrorChannel]);

  const handleClose = useCallback(() => {
    setView(initialView);
    setErrorChannel(initialView === 'unsent' ? initialErrorChannel ?? null : null);
    onClose();
  }, [initialView, initialErrorChannel, onClose]);

  const handleBack = useCallback(() => {
    setView('statistics');
    setErrorChannel(null);
  }, []);

  const openUnsentList = useCallback((channel: UnsentErrorChannel) => {
    setErrorChannel(channel);
    setView('unsent');
  }, []);

  const isUnsentView = view === 'unsent';
  const isTicket = contentType === SendContentType.Ticket;
  const unsentTitle = isTicket ? 'Неотправленные билеты' : 'Неотправленные приглашения';
  const { scrollRef, customScrollbar } = useCustomScrollbar<HTMLDivElement>(active, isUnsentView ? 1 : 0);

  if (!active) {
    return null;
  }

  return (
    <ModalWrapper active={active} {...MODAL_SETTINGS}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h2 className={styles.title}>{isUnsentView ? unsentTitle : 'Статистика отправки'}</h2>
          <IconButton onClick={handleClose} icon={<XmarkIcon size="24" />} size="small" aria-label="Закрыть" />
        </header>

        <div className={clsx(styles.body, isUnsentView && styles.bodyTall)}>
          <div className={clsx(styles.scrollHost, isUnsentView && styles.scrollHostTall)}>
            <div ref={scrollRef} className={clsx(styles.scrollContent, isUnsentView && styles.scrollContentTall)}>
              <ModalBodyContent
                messagesStats={messagesStats}
                contentType={contentType}
                isUnsentView={isUnsentView}
                errorChannel={errorChannel}
                onViewUnsent={openUnsentList}
              />
            </div>
            {customScrollbar.visible && (
              <>
                <div className={styles.scrollTrack} />
                <div
                  className={styles.scrollThumb}
                  style={{
                    height: `${customScrollbar.thumbHeight}px`,
                    transform: `translateY(${customScrollbar.thumbTop}px)`,
                  }}
                />
              </>
            )}
          </div>
        </div>

        <footer className={styles.footer}>
          {isUnsentView ? (
            <>
              <Button
                variant="secondary"
                size="small"
                onClick={handleBack}
                settings={{ padding: '8px 20px', fontSize: '14px' }}
              >
                Назад
              </Button>
              <Button
                variant="accent"
                size="small"
                onClick={handleClose}
                settings={{ padding: '8px 20px', fontSize: '14px' }}
              >
                Закрыть
              </Button>
            </>
          ) : (
            <Button
              variant="accent"
              size="small"
              onClick={handleClose}
              settings={{ padding: '8px 20px', fontSize: '14px' }}
            >
              На страницу мероприятия
            </Button>
          )}
        </footer>
      </div>
    </ModalWrapper>
  );
};
